import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { getStartEndDate } from "../ReportUtil";
import UsageQuickStats from "./UsageQuickStats";
import slugify from "slugify";
import { setIdsArray } from "../ReportUtil";
import { REACT_APP_ADMIN_LINK, useDMQuery } from "../../../utils";
import UsageReportTabs from "./UsageReportTabs";
import { toLower, isEqual } from "lodash";
import UsageGraphs from "./UsageGraphs";
import BackArrowIcon from "../../../shared/icons/BackArrowIcon";
import { QueryClient } from "@tanstack/react-query";
import {
  UsageParamsInterface,
  UsageLocalStorageInterface,
  SchoolData,
  SchoolsWithIntegral,
  TeacherData,
  SectionData,
} from "../ReportTypes";
import LoadingSkeloton from "../../utils/LoadingSkeleton";
import UsageDataScope from "./UsageDataScope";
import UsageDateSelector from "./UsageDateSelector";
import { endOfDay } from "date-fns";

const scopeTabs = ["Schools", "Teachers", "Sections"];

const StudentUsageContainer = ({
  schoolsWithIntegral,
  schoolData,
}: {
  schoolsWithIntegral: SchoolsWithIntegral[];
  schoolData: SchoolData[];
}) => {
  const params = useParams();
  const location = useLocation();
  const queryClient = new QueryClient();
  const { yesterday, lastWeek, thirtyDays, yearToDate } = getStartEndDate();
  const [stateData, setStateData] = useState<UsageLocalStorageInterface | null>(
    null
  );
  const [requestParams, setRequestParams] =
    useState<UsageParamsInterface | null>(null);

  // Remove usePrevious, use a ref instead
  const prevRequestParamsRef = useRef<UsageParamsInterface | null>(null);

  // Fetch Teacher data
  const { data: teacherData, isLoading: teacherDataLoading } = useDMQuery({
    path: "/admin_new/data/teacher",
    queryOptions: {
      staleTime: 1000 * 60 * 15,
    },
  });

  // Fetch Section data
  const { data: sectionData, isLoading: sectionDataLoading } = useDMQuery({
    path: "/admin_new/data/sections",
    queryOptions: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 15,
    },
  });

  // Fetch the usage data based on the request parameters
  const {
    data: usageData,
    refetch,
    isLoading,
  } = useDMQuery({
    cacheKey: [`/admin_new/data/usage`, JSON.stringify(requestParams)],
    path: `/admin_new/data/usage`,
    params: requestParams as Record<string, unknown>,
    method: "get",
    queryOptions: {
      enabled: false,
    },
  });

  // Initialize stateData from local storage or set default values
  useEffect(() => {
    const raw =
      JSON.parse(localStorage.getItem("usagePreferences") || "{}") || {};
    if (!stateData) {
      setStateData({
        startDate: raw.startDate ? new Date(raw.startDate) : yearToDate,
        endDate: raw.endDate ? new Date(raw.endDate) : yesterday,
        dateRange: raw.dateRange || "year-to-date",
        scope: raw.scope || "Schools",
        selectedSchools: raw.selectedSchools ?? null,
        selectedTeachers: raw.selectedTeachers ?? null,
        selectedSections: raw.selectedSections ?? null,
      });
    }
  }, [yearToDate, yesterday]);

  // Manage local storage for usage preferences
  useEffect(() => {
    if (stateData) {
      localStorage.setItem("usagePreferences", JSON.stringify(stateData));

      setRequestParams({
        start: `${(stateData.startDate?.getTime() ?? 0) / 1000}`,
        end: `${(stateData.endDate?.getTime() ?? 0) / 1000}`,
      });
    }
  }, [stateData]);

  /** Update requestParams when a selection is changed */
  useEffect(() => {
    const params = buildRequestParams(
      stateData,
      schoolsWithIntegral,
      teacherData,
      sectionData
    );
    if (params) {
      setRequestParams(params);
    }
  }, [schoolsWithIntegral, teacherData, sectionData, stateData]);

  // Invalidate queries and refetch when requestParams change
  useEffect(() => {
    const paramsChanged = !isEqual(prevRequestParamsRef.current, requestParams);

    if (paramsChanged && validParams(requestParams)) {
      queryClient.invalidateQueries({ queryKey: ["/admin_new/data/usage"] });
      refetch();
    }

    prevRequestParamsRef.current = requestParams;
  }, [requestParams, refetch]);

  // Sets Start and End Date when a date range selection is made
  useEffect(() => {
    if (stateData && stateData.dateRange === "last-week") {
      setStateData({
        ...stateData,
        startDate: lastWeek,
        endDate: yesterday,
      });
    } else if (stateData && stateData.dateRange === "thirty-days") {
      setStateData({
        ...stateData,
        startDate: thirtyDays,
        endDate: yesterday,
      });
    } else if (stateData && stateData.dateRange === "year-to-date") {
      setStateData({
        ...stateData,
        startDate: yearToDate,
        endDate: yesterday,
      });
    }
  }, [stateData?.dateRange]);

  // Wait for local storage to be parsed before rendering the component
  if (!stateData || teacherDataLoading || sectionDataLoading) {
    return <LoadingSkeloton />;
  }

  // Inline selected count/info for current scope
  const selectedCount =
    stateData.scope === "Sections"
      ? Object.keys(stateData.selectedSections || {}).length
      : stateData.scope === "Teachers"
      ? Object.keys(stateData.selectedTeachers || {}).length
      : Object.keys(stateData.selectedSchools || {}).length;

  let selectedInfo: string | number | undefined = selectedCount;
  if (
    stateData.scope === "Sections" &&
    sectionData &&
    selectedCount === sectionData.length
  ) {
    selectedInfo = "All";
  } else if (
    stateData.scope === "Teachers" &&
    teacherData &&
    selectedCount === teacherData.length
  ) {
    selectedInfo = "All";
  } else if (
    stateData.scope === "Schools" &&
    schoolsWithIntegral &&
    selectedCount === schoolsWithIntegral.length
  ) {
    selectedInfo = "All";
  }

  return (
    <>
      {location.pathname.includes(
        "/admin/reports/student-usage/select-scope"
      ) && (
        <>
          <div id="scope-header" className="border border-b-2 bg-white">
            <h2 className="px-6 pt-8 font-serif text-[24px] font-bold">
              Student Usage Reports
            </h2>

            <div className="py-8">
              <UsageQuickStats schoolData={schoolsWithIntegral} />
            </div>

            <div className="flex px-6">
              <nav
                className="-mb-px flex justify-between sm:justify-normal sm:space-x-14"
                aria-label="Tabs"
              >
                {scopeTabs.map((tab) => (
                  <Link
                    key={tab}
                    to={`${REACT_APP_ADMIN_LINK}/reports/student-usage/select-scope/${slugify(
                      tab,
                      {
                        lower: true,
                      }
                    )}`}
                    onClick={() => {
                      if (stateData.scope !== tab) {
                        setStateData({
                          ...stateData,
                          scope: tab as UsageLocalStorageInterface["scope"],
                        });
                      }
                    }}
                    replace
                    className={clsx(
                      slugify(tab, { lower: true }) === params.page
                        ? "border-dm-brand-blue-500 font-bold"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                      "border-b-4 px-1 py-4 text-center text-sm"
                    )}
                    aria-current={
                      slugify(tab, { lower: true }) === params.page
                        ? "page"
                        : undefined
                    }
                  >
                    <span className="px-2">{tab}</span>
                  </Link>
                ))}
              </nav>
            </div>
          </div>
          <div id="scope-body" className="p-6">
            <UsageDateSelector
              stateData={stateData}
              setStateData={setStateData}
              yesterday={yesterday}
            >
              <Link
                to={`${REACT_APP_ADMIN_LINK}/reports/student-usage/view-report`}
                replace
                className={clsx(
                  selectedCount ? "" : "pointer-events-none opacity-50",
                  "justify-center rounded-md border border-transparent bg-dm-brand-blue-500 px-8 py-2 text-sm font-bold text-white shadow-sm hover:bg-dm-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-dm-brand-blue-500 focus:ring-offset-2"
                )}
              >
                Generate Report
              </Link>
            </UsageDateSelector>

            <div id="select-data-scope" className="mt-8">
              <UsageDataScope
                schoolsWithIntegral={schoolsWithIntegral}
                stateData={stateData}
                setStateData={setStateData}
                teacherData={teacherData}
                sectionData={sectionData}
              />
            </div>
          </div>
        </>
      )}
      {location.pathname.includes(
        "/admin/reports/student-usage/view-report"
      ) && (
        <>
          <div
            id="report-header"
            className="space-y-6 border border-b-2 bg-white px-6"
          >
            <Link
              to={`${REACT_APP_ADMIN_LINK}/reports/student-usage/select-scope/${toLower(
                stateData.scope
              )}`}
              className="align-center inline-flex py-4 font-bold"
            >
              <BackArrowIcon classes="mt-1.5" />
              <span className="pl-2 text-dm-brand-blue-500">Back</span>
            </Link>
            <h2 className="font-serif text-[24px] font-bold">
              {`Report for ${stateData.scope} x Date`}
            </h2>
            <span className="">{`${selectedInfo} ${stateData.scope} Selected `}</span>

            <UsageDateSelector
              stateData={stateData}
              setStateData={setStateData}
              yesterday={yesterday}
            />

            <UsageReportTabs
              compareDisabled={selectedCount === 1 || selectedCount > 5}
            />
          </div>
          <div id="report-body" className="p-6">
            {usageData && (
              <UsageGraphs
                isLoading={isLoading}
                reportType={params.report_type || "problems-solved"}
                selectedDataScope={stateData.scope}
                schoolData={schoolData}
                sectionData={sectionData}
                teacherData={teacherData}
                startDate={stateData.startDate}
                endDate={stateData.endDate}
                data={usageData}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

function validParams(params: UsageParamsInterface | null | undefined): boolean {
  if (!params) return false;
  const hasIds =
    !!params.studentIds ||
    !!params.teacherIds ||
    !!params.schoolIds ||
    !!params.sectionIds ||
    params.district === "true";
  const hasDates = !!params.start && !!params.end;
  return hasIds && hasDates;
}

function buildRequestParams(
  stateData: UsageLocalStorageInterface | null,
  schoolsWithIntegral: SchoolsWithIntegral[],
  teacherData: TeacherData[],
  sectionData: SectionData[]
): UsageParamsInterface | null {
  if (!stateData) return null;

  let selectedIds: any[] = [];
  let key: keyof UsageParamsInterface | null = null;
  let dataSource: any[] = [];

  if (
    stateData.scope === "Schools" &&
    stateData.selectedSchools &&
    schoolsWithIntegral.length > 0
  ) {
    key = "schoolIds";
    dataSource = schoolsWithIntegral;
    selectedIds = setIdsArray(stateData.selectedSchools, dataSource, [
      "schoolid",
    ]).map((school) => school.schoolid);
  } else if (
    stateData.scope === "Teachers" &&
    stateData.selectedTeachers &&
    teacherData
  ) {
    key = "teacherIds";
    dataSource = teacherData;
    selectedIds = setIdsArray(stateData.selectedTeachers, dataSource, [
      "_id",
    ]).map((teacher) => teacher._id);
  } else if (
    stateData.scope === "Sections" &&
    stateData.selectedSections &&
    sectionData
  ) {
    key = "sectionIds";
    dataSource = sectionData;
    selectedIds = setIdsArray(stateData.selectedSections, dataSource, [
      "sectionId",
    ]).map((section) => section.sectionId);
  } else {
    return null;
  }

  const params: UsageParamsInterface = {};
  if (stateData.startDate?.getTime()) {
    params.start = Math.floor(stateData.startDate.getTime() / 1000).toString();
  }
  if (stateData.endDate?.getTime()) {
    params.end = Math.floor(
      endOfDay(stateData.endDate).getTime() / 1000
    ).toString();
  }
  if (selectedIds.length > 0 && key) {
    params[key] = selectedIds;
  }
  if (selectedIds.length > 1 && selectedIds.length <= 5) {
    params.series = selectedIds;
  }
  return params;
}

export default StudentUsageContainer;
